
import { Box } from "@mui/material"
import { ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { updateFocus, updateMode, updateModeData } from "../../redux/slices/editor"
import { ICommentContainer, IScriptComment, ISheetComment } from "../comments/CommentContainer"
import { FirebaseContext } from "../firebase/FirebaseProvider"
import UserAvatar from "../navbar/UserAvatar"
import NotificationItem from "./NotificationItem"

export interface CommentReplyDetails
{
    type: "commentReply"
    commentId: string
    postedByUserId: string
    project: string
}
interface SheetProps
{
    comment: ISheetComment
    postedByName: string
    project: string
    projectName: string
}

function CommentReplySheetNotification({ project, comment, postedByName, projectName }: SheetProps)
{
    const { get } = useContext(FirebaseContext);
    const [sheetName, setSheetName] = useState("");
    useEffect(() =>
    {
        if (!sheetName)
        {
            get(`sheets/${project}/${comment.sheetId}/name`).then((sheetName) =>
            {
                if (typeof sheetName === "string")
                    setSheetName(sheetName);
            });
        }
    }, [sheetName, comment.sheetId, get, project]);
    return <Box>{`${postedByName} posted a reply to a sheet comment in ${sheetName} ${projectName}`}</Box>
}

interface ScriptProps
{
    comment: IScriptComment
    postedByName: string
    project: string
    projectName: string
}

function CommentReplyScriptNotification({ project, comment, postedByName, projectName }: ScriptProps)
{
    return <Box>{`${postedByName} posted a reply to a script comment in ${projectName}`}</Box>
}

interface Props
{
    id: string
    read: boolean
    timestamp: number
    details: CommentReplyDetails
    children?: ReactNode
    updateMode: (mode: string) => void
    updateModeData: (modeData: { [key: string]: any }) => void
    updateFocus: (state: string) => void
    markAsRead: (id: string, read: boolean) => void
}

function CommentReplyNotification({ id, read, timestamp, details, children, updateMode, updateFocus, updateModeData, markAsRead }: Props)
{
    const { get } = useContext(FirebaseContext);
    const [name, setName] = useState("");
    const [projectName, setProjectName] = useState("");
    const [comment, setComment] = useState<ICommentContainer | null>(null);

    useEffect(() =>
    {
        if (!name)
        {
            get(`users/${details.postedByUserId}/name`)
                .then((name) => name && setName(name as string));
        }
    }, [details.postedByUserId, get, name]);

    useEffect(() =>
    {
        if (!projectName)
        {
            get(`projects/${details.project}/name`)
                .then((projectName) => typeof projectName === "string" && setProjectName(projectName));
        }
    }, [details.project, get, projectName]);

    useEffect(() =>
    {
        if (!comment)
        {
            get(`comments/${details.project}/${details.commentId}`)
                .then((comment) => comment && setComment(comment as ICommentContainer));
        }
    }, [comment, details, get]);

    const handleClickGoto = () =>
    {
        if (comment)
        {
            if (comment.type.name === "script")
            {
                updateFocus(comment.type.state);
                updateMode("writer");
            }
            else if (comment.type.name === "sheet")
            {
                updateMode("sheet");
                updateModeData({ selection: [comment.type.row], sheetId: comment.type.sheetId });
            }

            markAsRead(id, true);
        }
    }

    const messageContent = useMemo((): ReactElement | null =>
    {
        if (comment)
        {
            switch (comment.type.name)
            {
                case "script":
                    return <CommentReplyScriptNotification comment={comment.type} postedByName={name} project={details.project} projectName={projectName} />;
                case "sheet":
                    return <CommentReplySheetNotification comment={comment.type} postedByName={name} project={details.project} projectName={projectName} />
            }
        }

        return null;
    }, [comment, details.project, name, projectName]);

    return <NotificationItem onClick={handleClickGoto} icon={<UserAvatar user={{ name }} />} text={messageContent} read={read} timestamp={timestamp} >
        {children}
    </NotificationItem>
}

const mapStateToProps = (store: never) => ({
});
export default connect(mapStateToProps, {
    updateFocus,
    updateMode,
    updateModeData
})(CommentReplyNotification);